import React, { useEffect, useState } from 'react';
import FilterTable from './filterTable';
import { Link } from "react-router-dom";
import { WebViewerServersTableColumnCaptions } from './tableColumnCaptions';
import FavoriteItemColumnFormatter from './favoriteItemColumnFormatter';
import extractSearchParams from '../common/extractSearchParams';

const NotActivated = "Not activated";

const typeMapping = {
  1: 'personal',
  2: 'concurrent',
  3: 'unified',
}

const WebViewerServersTable = (props) => {
  let livingStateFilterValues = { 'all': '' };
  props.livingStates.forEach(livingState => livingStateFilterValues[livingState] = livingState);

  const typeValues = { 'all': '' };
  Object.keys(props.serverTypes).forEach(
    serverType => typeValues[serverType] = serverType
  );

  const unlimitedFormatter = (cell, row) => {
    if (row.type === 'unified' && cell === null) {
      return 'unlimited';
    }

    return cell;
  };

  const sum = (a, b) => {
    if (a === 'unlimited' || b === 'unlimited') {
      return 'unlimited';
    }

    return a + b;
  };

  const diff = (a, b) => {
    if (a === 'unlimited') {
      return 'unlimited';
    }

    if (b === 'unlimited') {
      return a;
    }

    if (a - b < 0) {
      return 0;
    }

    return a - b;
  };

  const calculateConnectionCount = (server) => {
    let { connectionSharingPolicy, connectionCount, linkConnectionCount } = server || {};

    const notShare = connectionSharingPolicy === 0;
    const useConnections = connectionSharingPolicy === 1;
    const useLinks = connectionSharingPolicy === 2;

    connectionCount = connectionCount !== null ? connectionCount : 'unlimited';
    linkConnectionCount = linkConnectionCount !== null ? linkConnectionCount : 'unlimited';

    let connections = '';
    let links = '';

    if (notShare) {
      connections = connectionCount;
      links = linkConnectionCount;
    }

    if (useConnections) {
      connections = `${connectionCount} (${diff(connectionCount, linkConnectionCount)})`;
      links = `${linkConnectionCount} (${sum(connectionCount, linkConnectionCount)})`;
    }

    if (useLinks) {
      connections = `${connectionCount} (${sum(connectionCount, linkConnectionCount)})`;
      links = `${linkConnectionCount} (${diff(connectionCount, linkConnectionCount)})`;
    }

    return { connections, links };
  };

  const columns = [
    {
      dataField: 'key',
      text: WebViewerServersTableColumnCaptions.key,
      sort: true,
      defaultValue: extractSearchParams(props.requestData.location.search, 'server.key'),
      filter_: { type: 'text' },
    },
    {
      dataField: 'productName',
      text: WebViewerServersTableColumnCaptions.productName,
      sort: true,
      filter_: { type: 'text' },
    },
    {
      dataField: 'productVersion',
      text: WebViewerServersTableColumnCaptions.productVersion,
      sort: true,
      filter_: { type: 'text' },
    },
    {
      dataField: 'generationDateTime',
      text: WebViewerServersTableColumnCaptions.generationDateTime,
      sort: true,
      filter_: { type: 'datetime' },
    },
    {
      dataField: 'activationDateTime',
      text: WebViewerServersTableColumnCaptions.activationDateTime,
      sort: true,
      filter_: { type: 'datetime' },
    },
    {
      dataField: 'expirationDateTime',
      text: WebViewerServersTableColumnCaptions.expirationDateTime,
      sort: true,
      filter_: { type: 'datetime' },
      formatter: unlimitedFormatter,
    },
    {
      dataField: 'deviceKey',
      text: 'Device key',
      sort: true,
      filter_: { type: 'text' },
      headerStyle: () => ({ width: '12em' }),
      formatter: (cell) => {
        const isKey = cell !== NotActivated;
        return isKey ? <Link to={`/history?device.key=${cell}`}>{cell}</Link> : cell;
      }
    },
    {
      dataField: 'type',
      text: WebViewerServersTableColumnCaptions.type,
      sort: true,
      filter_: {
        type: 'select',
        values: typeValues,
      },
      headerStyle: () => ({ width: '150px' }),
    },
    {
      dataField: 'connectionCount',
      text: WebViewerServersTableColumnCaptions.connectionCount,
      sort: true,
      filter_: { type: 'text' },
      headerStyle: () => ({ width: '50px' }),
    },
    {
      dataField: 'linkConnectionCount',
      text: WebViewerServersTableColumnCaptions.linkConnectionCount,
      sort: true,
      filter_: { type: 'text' },
      headerStyle: () => ({ width: '50px' }),
    },
    {
      dataField: 'linkCount',
      text: WebViewerServersTableColumnCaptions.linkCount,
      sort: true,
      filter_: { type: 'text' },
      headerStyle: () => ({ width: '50px' }),
    },
    {
      dataField: 'usedLinkCount',
      text: WebViewerServersTableColumnCaptions.usedLinkCount,
      sort: true,
      filter_: { type: 'text' },
      headerStyle: () => ({ width: '50px' }),
    },
    {
      dataField: 'client.fullName',
      text: WebViewerServersTableColumnCaptions.client,
      sort: true,
      filter_: { type: 'text' },
    },
    {
      dataField: 'livingState',
      text: WebViewerServersTableColumnCaptions.livingState,
      sort: true,
      filter_: {
        type: 'select',
        values: livingStateFilterValues
      },
    },
    {
      dataField: 'isFavorite',
      text: WebViewerServersTableColumnCaptions.isFavorite,
      sort: false,
      align: 'center',
      filter_: {
        type: 'select',
        values: {
          'all': '',
          'favorite': 'true',
        }
      },
      headerStyle: () => ({ width: '35px' }),
      formatter: (cell, row) => <FavoriteItemColumnFormatter
        cell={cell} row={row}
        actions={props.actions} />,
    }
  ];

  const [servers, setServers] = useState(props.webViewerServers);

  useEffect(() => {
    const servers = props.webViewerServers.map((server) => {
      const data = {
        ...server,
        deviceKey: server.device ? server.device.key : NotActivated,
        type: typeMapping[server.type],
      };

      if (server.type === 3) {
        const { connections, links } = calculateConnectionCount(server);
        data.connectionCount = connections;
        data.linkConnectionCount = links;
      }

      return data;
    });

    setServers(servers);
  }, [props.webViewerServers]);

  const defaultSorted = [
    {
      dataField: 'generationDateTime',
      order: 'desc',
    },
  ];

  return (
    <FilterTable
      {...props}
      objectName={'webViewerServers'}
      data={servers}
      columns={columns}
      noDataIndication="There are no web viewer servers"
      defaultSorted={defaultSorted}
    />
  );
}

export default WebViewerServersTable;


// WEBPACK FOOTER //
// ./src/components/webViewerServersTable.js